import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as BadgeIcon,
  Cancel as RemoveIcon,
  Edit as EditIcon,
  Insights as StatsIcon,
  Refresh as RefreshIcon,
  Schedule as RenewIcon,
} from '@mui/icons-material';
import service, {
  FundadorPayload,
  FundadorStats,
  ParceiroActivityStats,
  ParceiroFundador,
} from '../../services/marketing-parceiros.service';
import hubService, { Parceiro } from '../../services/hub.service';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';
import {
  StorefrontOutlined,
  WarningAmberOutlined,
  PaidOutlined,
  LocationCityOutlined,
} from '@mui/icons-material';
import { nowCv } from '../../utils/date';

const formatCve = (v?: number | null) => (v == null ? '—' : `${Number(v).toLocaleString('pt-PT')} CVE`);
const formatDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—');
// Datas do estatuto de fundador são hora de parede de CV → mostrar em UTC.
const formatDateParede = (v?: string | null) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—');

export default function ParceirosFundadores() {
  const [fundadores, setFundadores] = useState<ParceiroFundador[]>([]);
  const [allParceiros, setAllParceiros] = useState<Parceiro[]>([]);
  const [stats, setStats] = useState<FundadorStats | null>(null);
  const [cidadeFilter, setCidadeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ParceiroFundador | null>(null);
  const [form, setForm] = useState<FundadorPayload>({});

  const [promoteOpen, setPromoteOpen] = useState(false);
  const [selectedParceiroId, setSelectedParceiroId] = useState<number | ''>('');

  const [statsOpen, setStatsOpen] = useState(false);
  const [activityStats, setActivityStats] = useState<ParceiroActivityStats | null>(null);
  const [statsParceiro, setStatsParceiro] = useState<ParceiroFundador | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s, p] = await Promise.all([
        service.list(cidadeFilter || undefined),
        service.stats().catch(() => null),
        hubService.getParceiros().catch(() => []),
      ]);
      setFundadores(f);
      setStats(s);
      setAllParceiros(p);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [cidadeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (p: ParceiroFundador) => {
    setEditing(p);
    setForm({
      is_fundador: p.is_fundador === 1,
      cidade_fundador: p.cidade_fundador || '',
      data_fundador_inicio: p.data_fundador_inicio?.slice(0, 10) || '',
      data_fundador_expiracao: p.data_fundador_expiracao?.slice(0, 10) || '',
      badge_visivel: p.badge_visivel === 1,
      posicao_destaque: p.posicao_destaque,
      limite_marketing_cve: p.limite_marketing_cve,
      entidade_id: p.entidade_id,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await service.setFundador(editing.id, form);
      setToast({ msg: 'Atualizado', severity: 'success' });
      setEditOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const promote = async () => {
    if (!selectedParceiroId) return;
    try {
      await service.setFundador(Number(selectedParceiroId), {
        is_fundador: true,
        cidade_fundador: 'Praia',
        badge_visivel: true,
      });
      setToast({ msg: 'Parceiro promovido a Fundador', severity: 'success' });
      setPromoteOpen(false);
      setSelectedParceiroId('');
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const removeFundador = async (p: ParceiroFundador) => {
    if (!window.confirm(`Remover estatuto de fundador de "${p.nome}"?`)) return;
    try {
      await service.setFundador(p.id, { is_fundador: false });
      setToast({ msg: 'Estatuto removido', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const renew = async (p: ParceiroFundador) => {
    const mesesStr = window.prompt('Estender expiração em quantos meses?', '6');
    if (!mesesStr) return;
    const meses = parseInt(mesesStr, 10);
    if (!meses || meses < 1) return;
    try {
      await service.renovar(p.id, meses);
      setToast({ msg: `+${meses} meses`, severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const showStats = async (p: ParceiroFundador) => {
    setStatsParceiro(p);
    setStatsOpen(true);
    setActivityStats(null);
    try {
      setActivityStats(await service.activityStats(p.id));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const isExpired = (p: ParceiroFundador) =>
    p.data_fundador_expiracao && new Date(p.data_fundador_expiracao) < nowCv();
  const isExpiringSoon = (p: ParceiroFundador) => {
    if (!p.data_fundador_expiracao) return false;
    const d = new Date(p.data_fundador_expiracao);
    const soon = nowCv();
    soon.setDate(soon.getDate() + 30);
    return d < soon && d >= nowCv();
  };

  const nonFundadores = allParceiros.filter((p) => !fundadores.find((f) => f.id === p.id));

  return (
    <Box>
      <PageHeader
        title="Parceiros Fundadores"
        subtitle="Estatuto especial com marketing gratuito e posição destaque"
        icon={<BadgeIcon />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load}>Atualizar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPromoteOpen(true)}>Promover Parceiro</Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Estatuto de <b>Fundador Pagali</b> (Passo 3 do plano) — marketing gratuito, posição de destaque,
        badge visível. Ligue o parceiro a uma <b>Entidade</b> para ver estatísticas de atividade.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <StatCard icon={<StorefrontOutlined />} label="Total fundadores" value={stats?.total ?? '—'} color="primary" />
        <StatCard
          icon={<WarningAmberOutlined />}
          label="Expiram em 30 dias"
          value={stats?.expirando_30d ?? '—'}
          color="warning"
        />
        <StatCard
          icon={<PaidOutlined />}
          label="Marketing gasto"
          value={formatCve(stats?.marketing_gasto)}
          color="info"
          helper={
            stats?.marketing_total
              ? `de ${formatCve(stats.marketing_total)} (${Math.round((stats.marketing_gasto / stats.marketing_total) * 100)}%)`
              : undefined
          }
        />
        <StatCard
          icon={<LocationCityOutlined />}
          label="Cidades"
          color="secondary"
          value={
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25, gap: 0.5 }}>
              {stats?.por_cidade?.length ? (
                stats.por_cidade.map((c) => <Chip key={c.cidade} size="small" label={`${c.cidade}: ${c.n}`} />)
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Stack>
          }
        />
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Cidade</InputLabel>
          <Select
            label="Cidade"
            value={cidadeFilter}
            onChange={(e) => setCidadeFilter(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="Praia">Praia</MenuItem>
            <MenuItem value="Mindelo">Mindelo</MenuItem>
            <MenuItem value="Assomada">Assomada</MenuItem>
            <MenuItem value="Espargos">Espargos</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Parceiro</TableCell>
                <TableCell>Cidade</TableCell>
                <TableCell>Estatuto</TableCell>
                <TableCell>Expiração</TableCell>
                <TableCell align="right">Marketing</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : fundadores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<BadgeIcon />}
                      title="Ainda sem parceiros fundadores"
                      description="Promova um parceiro para lhe dar o estatuto fundador: marketing gratuito, badge visível e posição destaque na app."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPromoteOpen(true)}>
                          Promover Parceiro
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                fundadores.map((p) => {
                  const usadoPct = p.limite_marketing_cve
                    ? Math.min(100, Math.round((Number(p.saldo_marketing_usado) / Number(p.limite_marketing_cve)) * 100))
                    : 0;
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        {p.posicao_destaque ? (
                          <Chip size="small" label={`#${p.posicao_destaque}`} color="warning" />
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {p.badge_visivel === 1 && <BadgeIcon color="warning" fontSize="small" />}
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{p.nome}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.categoria}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{p.cidade_fundador || '—'}</TableCell>
                      <TableCell>
                        {isExpired(p) ? (
                          <Chip size="small" label="Expirado" color="error" />
                        ) : isExpiringSoon(p) ? (
                          <Chip size="small" label="Expira em breve" color="warning" />
                        ) : (
                          <Chip size="small" label="Ativo" color="success" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDateParede(p.data_fundador_inicio)} → {formatDateParede(p.data_fundador_expiracao)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 140 }}>
                        {p.limite_marketing_cve ? (
                          <>
                            <Typography variant="caption">
                              {formatCve(p.saldo_marketing_usado)} / {formatCve(p.limite_marketing_cve)}
                            </Typography>
                            <LinearProgress variant="determinate" value={usadoPct} sx={{ mt: 0.5 }} />
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Sem limite</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Stats atividade">
                          <IconButton size="small" onClick={() => showStats(p)}>
                            <StatsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Renovar expiração">
                          <IconButton size="small" onClick={() => renew(p)}>
                            <RenewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover estatuto">
                          <IconButton size="small" color="error" onClick={() => removeFundador(p)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Promote dialog */}
      <Dialog open={promoteOpen} onClose={() => setPromoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Promover Parceiro a Fundador</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Parceiro</InputLabel>
            <Select
              label="Parceiro"
              value={selectedParceiroId}
              onChange={(e) => setSelectedParceiroId(e.target.value as number)}
            >
              {nonFundadores.length === 0 ? (
                <MenuItem disabled value="">
                  Todos os parceiros já são fundadores
                </MenuItem>
              ) : (
                nonFundadores.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nome} ({p.categoria})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            Estatuto atribuído com cidade padrão "Praia". Edite depois para configurar expiração e limite de marketing.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={promote} disabled={!selectedParceiroId}>
            Promover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Editar Fundador — {editing?.nome}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                label="Cidade"
                placeholder="Ex: Praia, Mindelo"
                value={form.cidade_fundador || ''}
                onChange={(e) => setForm({ ...form, cidade_fundador: e.target.value })}
              />
              <TextField
                fullWidth
                type="number"
                label="Posição destaque"
                value={form.posicao_destaque ?? ''}
                onChange={(e) => setForm({ ...form, posicao_destaque: e.target.value === '' ? null : Number(e.target.value) })}
                helperText="1 = topo"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Início do estatuto"
                InputLabelProps={{ shrink: true }}
                value={form.data_fundador_inicio || ''}
                onChange={(e) => setForm({ ...form, data_fundador_inicio: e.target.value })}
              />
              <TextField
                fullWidth
                type="date"
                label="Expiração"
                InputLabelProps={{ shrink: true }}
                value={form.data_fundador_expiracao || ''}
                onChange={(e) => setForm({ ...form, data_fundador_expiracao: e.target.value })}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Limite marketing (CVE/mês)"
                value={form.limite_marketing_cve ?? ''}
                onChange={(e) =>
                  setForm({ ...form, limite_marketing_cve: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
              <TextField
                fullWidth
                type="number"
                label="Entidade ID"
                placeholder="ID em pg_entidade"
                value={form.entidade_id ?? ''}
                onChange={(e) => setForm({ ...form, entidade_id: e.target.value === '' ? null : Number(e.target.value) })}
                helperText="Necessário para stats de atividade"
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={form.badge_visivel !== false}
                  onChange={(e) => setForm({ ...form, badge_visivel: e.target.checked })}
                />
              }
              label="Badge visível na app"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Stats dialog */}
      <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Atividade Pagali — {statsParceiro?.nome}</DialogTitle>
        <DialogContent dividers>
          {!activityStats ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : activityStats.aviso ? (
            <Alert severity="warning">{activityStats.aviso}</Alert>
          ) : (
            <Grid container spacing={3}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Clientes únicos Pagali</Typography>
                <Typography variant="h5">{activityStats.clientes_pagali}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Transações</Typography>
                <Typography variant="h5">{activityStats.num_transacoes}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Ticket médio</Typography>
                <Typography variant="h5">{formatCve(activityStats.ticket_medio)}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Total vendido</Typography>
                <Typography variant="h5">{formatCve(activityStats.total_vendido_cve)}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  Última transação: {formatDate(activityStats.ultima_transacao)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
